<template>
  <Widget
    preventDataFetch
    widgetName="predictedFinancialImpact"
    :customLoading="isLoading"
    :customWidgetIsEmpty="isEmpty"
    @locationOptionChange="handleLocationOptionChange"
    @timeOptionChange="handleTimeOptionChange"
  >
    <template v-slot:content>
      <div class="px-6">
        <v-row class="ma-0 pa-0">
          <v-col
            cols="12"
            md="5"
            class="pa-0 d-flex flex-column justify-center align-start"
          >
            <div
              class="me-6"
              data-testid="predictedFinancialImpactWidget-totalContainer"
            >
              <p
                class="ma-0 pa-0 blue-grey--text text--darken-1 body-2 mb-2"
                data-testid="predictedFinancialImpactWidget-label"
              >
                {{ $t('insights.predicted_financial_impact.value_label') }}
              </p>
              <p
                class="ma-0 pa-0 blue-grey--text text--darken-3 font-size-24 font-weight-700"
                style="line-height: 24px"
                data-testid="predictedFinancialImpactWidget-value"
              >
                {{ formattedTotal }}
              </p>
            </div>
          </v-col>
          <v-col cols="12" md="7" class="pa-0">
            <p
              class="ma-0 pa-0 blue-grey--text text--darken-2 font-size-12 font-weight-600 mb-3"
              style="line-height: 16px; letter-spacing: 0.4px"
              data-testid="predictedFinancialImpactWidget-topRevsTitle"
            >
              {{ $t('insights.predicted_financial_impact.top_revs_title') }}
            </p>
            <div class="top-revs-table-container">
              <v-data-table
                class="rounded top-revs-table"
                mobile-breakpoint="0"
                disable-sort
                disable-filtering
                disable-pagination
                hide-default-footer
                :headers="headers"
                :items="items"
              >
                <template v-slot:item.name="{ item }">
                  <p
                    v-snip="2"
                    class="blue-grey--text text--darken-3 cursor-pointer font-size-12 font-weight-500 link-td ma-0 pa-0 top-rev-name"
                    data-testid="predictedFinancialImpactWidget-revLink"
                    :title="item.name"
                    @click="navigateToRev(item)"
                  >
                    {{ item.name }}
                  </p>
                </template>

                <template v-slot:item.formattedStatus="{ item }">
                  <span
                    class="blue-grey--text text--darken-3 font-size-10 font-weight-500"
                    data-testid="predictedFinancialImpactWidget-status"
                  >
                    {{ item.formattedStatus }}
                  </span>
                </template>

                <template v-slot:item.formattedImpact="{ item }">
                  <span
                    class="blue-grey--text text--darken-3 font-size-10 font-weight-500"
                    data-testid="predictedFinancialImpactWidget-impact"
                  >
                    {{ item.formattedImpact }}
                  </span>
                </template>
              </v-data-table>
            </div>
          </v-col>
        </v-row>
      </div>
    </template>

    <template v-slot:widget-actions>
      <Button
        color="primary"
        data-testid="predictedFinancialImpactWidget-button-exploreRevs"
        data-tourid="predicted-financial-impact-explore-revs"
        plain
        size="small"
        :text="$t('insights.explore_revs')"
        @click="goToRevs"
      />
    </template>
  </Widget>
</template>

<script>
import _ from 'lodash'
import { utils } from 'frontend-js-shared'
import { mapGetters } from 'vuex'

import api from '@/api'
import Button from '@/components/UI/Button.vue'
import mixin from '@/mixins/mixin.vue'
import moneyMixin from '@/mixins/moneyMixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import { WIDGET_DATE_RANGE_TO_EXPLORE_OPTION } from './constants'
import Widget from './SharedComponents/WidgetBase.vue'

export default {
  name: 'PredictedFinancialImpactWidget',

  mixins: [mixin, moneyMixin, trackerMixin],

  components: {
    Button,
    Widget,
  },

  data() {
    return {
      isLoading: false,
      selectedSite: '',
      selectedTimeOption: 'l30days',
      totalPredictedFinancialImpactInUSD: null,
      topRevs: [],
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'siteId']),

    isEmpty() {
      return (
        !_.isNumber(this.totalPredictedFinancialImpactInUSD) &&
        _.isEmpty(this.topRevs)
      )
    },

    headers() {
      return [
        {
          text: this.$t('insights.predicted_financial_impact.rev'),
          align: 'start',
          sortable: false,
          value: 'name',
          class:
            'blue-grey--text text--darken-1 font-size-10 font-weight-600 px-2 py-0 top-revs-header',
          cellClass: 'font-weight-500 ps-2 ma-0 pa-0 py-1',
        },
        {
          text: this.$t('insights.predicted_financial_impact.status'),
          align: 'start',
          sortable: false,
          value: 'formattedStatus',
          class:
            'blue-grey--text text--darken-1 font-size-10 font-weight-600 px-2 py-0 top-revs-header',
          cellClass:
            'blue-grey--text text--darken-3 font-size-10 font-weight-500 ps-2 ma-0 pa-0',
          width: '96px',
        },
        {
          text: this.$t('insights.predicted_financial_impact.impact_in_usd'),
          align: 'start',
          sortable: false,
          value: 'formattedImpact',
          class:
            'blue-grey--text text--darken-1 font-size-10 font-weight-600 px-2 py-0 top-revs-header',
          cellClass:
            'blue-grey--text text--darken-3 font-size-10 font-weight-500 ps-2 ma-0 pa-0',
          width: '96px',
        },
      ]
    },

    items() {
      return this.topRevs.slice(0, 4).map(rev => ({
        ...rev,
        formattedImpact: this.formatImpact(rev.predictedFinancialImpactInUSD),
        formattedStatus: this.formatStatus(rev.status),
        routeName: this.$constants.revTypeRouteNames[rev.revType],
      }))
    },

    formattedTotal() {
      return this.formatImpact(this.totalPredictedFinancialImpactInUSD)
    },
  },

  mounted() {
    this.selectedSite = this.siteId
    this.fetchData()
  },

  methods: {
    formatImpact(value) {
      return this.formatToUserCurrency(value)
    },

    formatStatus(status) {
      if (!status) return '—'

      const statusKey = `common.entity_status.${_.snakeCase(status)}`
      const translated = this.$t(statusKey)

      return translated === statusKey ? '—' : translated
    },

    handleLocationOptionChange(option) {
      this.selectedSite = option
      this.fetchData()
    },

    handleTimeOptionChange(option) {
      this.selectedTimeOption = option
      this.fetchData()
    },

    navigateToRev(item) {
      if (!item.routeName) return

      this.openInNewTab({
        name: item.routeName,
        params: {
          organizationId: this.organizationId,
          revId: item.id,
        },
      })
    },

    goToRevs() {
      this.trackInsightsWidgetNavigate({
        widget: 'predictedFinancialImpact',
        action: 'exploreRevsClick',
        selectedSite: this.selectedSite,
      })

      const hasSelectedSite =
        !_.isEmpty(this.selectedSite) && this.selectedSite !== 'global'

      const query = {
        sort: 'predictedFinancialImpactInUSD-desc',
        sites: hasSelectedSite ? [this.selectedSite] : [],
      }

      const exploreDateOption =
        WIDGET_DATE_RANGE_TO_EXPLORE_OPTION[this.selectedTimeOption]

      if (exploreDateOption) {
        const { startDate, endDate } = utils.dates.getPredefinedDateRange(
          exploreDateOption
        )

        query.createdAtOption = exploreDateOption
        query.createdAt = [
          utils.dates.getStartOfDay(startDate),
          utils.dates.getEndOfDay(endDate),
        ]
      }

      this.openInNewTab({ name: 'explore', query })
    },

    async fetchData() {
      try {
        this.isLoading = true
        const query = {
          dateRange: this.selectedTimeOption,
        }

        if (this.selectedSite && this.selectedSite !== 'global') {
          query.site = this.selectedSite
        }

        const response = await api.insights.getPredictedFinancialImpactInsight({
          organizationId: this.organizationId,
          query,
        })

        this.totalPredictedFinancialImpactInUSD =
          response?.data?.data?.totalPredictedFinancialImpactInUSD ?? null
        this.topRevs = response?.data?.data?.topRevs ?? []
      } catch (err) {
        this.handleRequestError(err)
        this.totalPredictedFinancialImpactInUSD = null
        this.topRevs = []
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
.top-revs-table-container {
  .top-revs-table {
    border: 1px solid $color-blue-grey-lighten2;
  }

  .top-revs-header {
    height: 23px !important;
  }

  .top-rev-name {
    display: inline-block;
    max-width: 100%;
    line-height: 16px;
  }

  .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 30px !important;
  }
}
</style>
